import { useCallback, useState } from "react";

/**
 * Which departments are unfolded in the sidebar tree (#9304).
 *
 * Per browser, like the theme and the saved backlog filters: it is a reading
 * position, not a setting worth a column on `profiles`. Storage can throw in
 * private mode or with site data blocked; a sidebar that forgets its folds is
 * fine, one that crashes is not.
 */

const STORAGE_KEY = "nav-expanded-departments";

const readStored = (): Set<string> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : []);
  } catch {
    return new Set();
  }
};

const writeStored = (ids: ReadonlySet<string>) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Not remembered across reloads; the in-memory state still works.
  }
};

export const useExpandedDepartments = () => {
  const [expanded, setExpanded] = useState<Set<string>>(readStored);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeStored(next);
      return next;
    });
  }, []);

  /** Unfold every one of `ids` — the path down to the selected department. */
  const reveal = useCallback((ids: Iterable<string>) => {
    setExpanded((prev) => {
      const missing = [...ids].filter((id) => !prev.has(id));
      if (missing.length === 0) return prev;
      const next = new Set([...prev, ...missing]);
      writeStored(next);
      return next;
    });
  }, []);

  return { expanded, toggle, reveal };
};
